import {
    Button,
    DatePicker,
    Flex,
    Form,
    Input,
    InputNumber,
    Modal,
    Radio,
    Select
} from 'antd';
import {
    ReactElement,
    useContext,
    useEffect,
    useState
} from 'react';
import {
    useLazyQuery,
    useMutation,
    useQuery
} from '@apollo/client';
import {
    useTranslation
} from 'react-i18next';
import {
    DELETE_RESERVATION,
    UPDATE_RESERVATION
} from '../apollo/mutation/book.mutation';
import {
    RETRIEVE_AVAILABILITY_BOOK,
    RETRIEVE_TIME_SLOTS
} from '../apollo/query/time-slot.query';
import {
    RETRIEVE_MENUS
} from '../apollo/query/menu.query';
import type {
    Dayjs
} from "dayjs";
import {
    NotificationContext
} from '../context/notification-provider';
import dayjs from "dayjs";
import TextArea from 'antd/es/input/TextArea';

const { Option } = Select;

const ReservationDetail = (props: any) => {
    /** Retrieve component properties */
    const { appointment, nIDRestaurant, setSelectedAppointment, nIDUser } = props;
    /** Form utils */
    const [form] = Form.useForm();
    /** Translation utils */
    const { i18n, t } = useTranslation();
    /** Number of guests information */
    const [guestsNumber, setGuestsNumber] = useState<number>(0);
    /** State to manage the decision about the menu for the users */
    const [wantMenu, setWantMenu] = useState<boolean>(false);
    /** Manage to retrieve all the available menus of the restaurant */
    const { data } = useQuery(RETRIEVE_MENUS, { variables: { nIDRestaurant: nIDRestaurant } });
    /** Manage to retrieve the time slots available */
    const { data: times } = useQuery(RETRIEVE_TIME_SLOTS, { fetchPolicy: 'no-cache', variables: { nIDRestaurant: nIDRestaurant } });
    /** The time slot selected */
    const [timeSlot, setTimeSlot] = useState<any>(null);
    /** Manage to check if a new booking on a time slot is available */
    const [ checkAvailability ] = useLazyQuery(RETRIEVE_AVAILABILITY_BOOK, { fetchPolicy: 'no-cache' }  );
    /** Manage to insert a new reservation */
    const [ updateReservation, { data: bookingResponse } ] = useMutation(UPDATE_RESERVATION, { refetchQueries: ['gqlRetrieveBooksByRestaurant'] } );
    const [ deleteReservation, { data: deletionResponse } ] = useMutation(DELETE_RESERVATION, { refetchQueries: ['gqlRetrieveBooksByRestaurant'] } );
    /** Notification utilities */
    const { showNotification } = useContext(NotificationContext);

    /** Handle on close modal */
    const onClose = (): void => {
        form.resetFields();
        setGuestsNumber(0);
        setWantMenu(false);
        setTimeSlot(null);
        setSelectedAppointment(null);
    }

    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( bookingResponse?.code == 200 ) {
            showNotification({ severity: 'success', title: t('book.booking-updated') });
            onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingResponse]);
    
    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( deletionResponse?.code == 200 ) {
            showNotification({ severity: 'success', title: t('book.booking-cancelled') });
            onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletionResponse]);

    /** Handle the change values of the form */
    const handleValuesChange = (changedValues: any) => {
        if ('nGuests' in changedValues) {
            setGuestsNumber(changedValues.nGuests);
        } else if ('bMenu' in changedValues) {
            setWantMenu(changedValues.bMenu);
        }
    };

    /** Check when to disable the dates */
    const disabledDate = (current: Dayjs) => {
        return current && current < dayjs().startOf("day");
    };

    /** Manage the change of time slots and the creation of the available times */
    const onTimeSlot = (timeSlot: number): void => {
        // eslint-disable-next-line eqeqeq
        const slot = times?.data?.find((x: any) => x.nID == timeSlot);
        const intervals = [];

        let start = new Date(`1970-01-01T${slot.tStart}:00`);
        const end = new Date(`1970-01-01T${slot.tEnd}:00`);

        while (start < end) {
            let next = new Date(start.getTime() + 15 * 60000);
            if (next > end) break;
            intervals.push(start.toTimeString().slice(0, 5));
            start = next;
        }
        form.resetFields(['time']);
        setTimeSlot(intervals);
    }

    useEffect(() => {
        if(times?.data && appointment) {
            onTimeSlot(appointment.nIDTimeSlot);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [times])
    
    /** Manage to submit the formulary */
    const handleSubmit = async (values: any) => {
        const { date, ...rest } = values;
        const bookInfo = {
            ...rest,
            dDate: dayjs(date).format("YYYY-MM-DD"),
            bMenu: rest.bMenu ?? false,
            nIDMenu: rest.nIDMenu ?? null,
            sSetting: rest.sSetting ?? null,
            sAccessNeeds: rest.sAccessNeeds ?? null,
            sChildrenNeeds: rest.sChildrenNeeds ?? null,
            sDescription: rest.sDescription ?? null,
        };
        const { data } = await checkAvailability({ variables: {
            timeSlot: bookInfo.nIDTimeSlot,
            date: dayjs(bookInfo.date).format("YYYY-MM-DD")
        } });

        // eslint-disable-next-line eqeqeq
        if( data?.code == 200 ) {
            updateReservation({ variables: { nID: appointment.nID, nIDUser: nIDUser, nIDRestaurant: nIDRestaurant, book: bookInfo  }} );
        } else {
            showNotification({
                timeout: 5000,
                severity: 'warning',
                title: t('book.no-more-reservations')
            });
        }
    };

    const onDelete = (): void => {
        deleteReservation({ variables: { nID: appointment.nID, nIDUser: nIDUser, nIDRestaurant: nIDRestaurant} });
    }

    const renderGeneralInformation = (): ReactElement => (
        <Flex vertical gap={0} style={{ maxHeight: 300, overflowY: 'auto' }}>
            <Form.Item
                label={ t('label.guest-name') }
                name="sName"
                style={{ width: '100%' }}
                rules={[{ required: true, message: t('label.enter-name') }]}
            >
                <Input
                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                    placeholder={ t('label.placeholder-name') }
                />
            </Form.Item>

            <Form.Item
                label={ t('label.phone') }
                name="sPhone"
                style={{ width: '100%' }}
                rules={[
                    { required: true, message: t('label.enter-phone') },
                ]}
            >
                <Input
                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                    placeholder={ t('label.placeholder-phone') }
                />
            </Form.Item>

            <Form.Item
                label={ t('label.date') }
                name="date"
                style={{ width: '100%' }}
                rules={[{ required: true, message: t('label.enter-date') }]}
            >
                <DatePicker
                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                    disabledDate={disabledDate} />
            </Form.Item>

            <Form.Item
                label={ t('label.number-guests') }
                name="nGuests"
                style={{ width: '100%' }}
                rules={[
                    { required: true, message: t('label.enter-guests') },
                ]}
            >
                <InputNumber
                    min={1}
                    max={20}
                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                    placeholder={ t('label.placeholder-guests') }
                />
            </Form.Item>
            <Form.Item
                label={ t('label.time-slot') }
                name="nIDTimeSlot"
                style={{ width: '100%' }}
                rules={[
                    { required: true, message: t('label.select-time-slot') },
                ]}
            >
                <Radio.Group buttonStyle="solid" optionType="button" onChange={(e: any) => onTimeSlot(e.target.value)}>
                    { times?.data?.map((time: any) => ( <Radio key={time.nID} value={time.nID}> { `${time.tStart} - ${time.tEnd}` } </Radio> ))}
                </Radio.Group>
            </Form.Item>
            { timeSlot &&
                <Form.Item
                    label={ t('label.time') }
                    name="sTime"
                    style={{ width: '100%' }}
                    rules={[
                        { required: true, message: t('label.select-time') },
                    ]}
                >
                    <Radio.Group buttonStyle="solid" optionType="button" >
                        { timeSlot?.map((time: any) => ( <Radio key={time} value={time}> { time  } </Radio> ))}
                    </Radio.Group>
                </Form.Item>
            }
            <Form.Item
                label={ t('label.setting-preference') }
                name="sSetting"
                style={{ width: '100%' }}
                rules={[
                    {
                        required: true,
                        message: t('label.enter-setting-preference'),
                    },
                ]}
            >
                <Select placeholder={ t('label.placeholder-seating-preference') } style={{ height: 50 }}>
                    <Option value="indoor"> { t('label.indoor') } </Option>
                    <Option value="outdoor"> { t('label.outdoor') } </Option>
                </Select>
            </Form.Item>

            { guestsNumber >= 16 &&
                <Form.Item
                    label={ t('label.choose') }
                    name="bMenu"
                    style={{ width: '100%' }}
                >
                    <Radio.Group optionType="button" buttonStyle="solid">
                        <Radio value={true}> { t('label.yes') } </Radio>
                        <Radio value={false}> { t('label.no') } </Radio>
                    </Radio.Group>
                </Form.Item>
            }
            { wantMenu &&
                <Form.Item
                    label={ t('label.menu-preference') }
                    name="nIDMenu"
                    style={{ width: '100%' }}
                >
                    <Select placeholder={ t('label.menu-preference-placeholder') } style={{ height: 50 }}>
                        { data?.data.map((menu: any) => <Option key={menu.nId} value={menu.nID}> { menu.languages['name-' + i18n.language] } </Option> ) }
                    </Select>
                </Form.Item>
            }

            <Form.Item name="sDescription" label={ t('label.special-req-dietary') } style={{ width: '100%' }}>
                <TextArea
                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                    autoSize
                    placeholder={ t('label.placeholder-alergies') }
                />
            </Form.Item>
        </Flex>
    );

    return (
        <Modal
            title={ t('book.title') }
            open={!!appointment}
            onCancel={() => setSelectedAppointment(null)}
            footer={[
                <Button key='close' type="default" onClick={() => setSelectedAppointment(null)}> { t('buttons.close') } </Button>,
                <Button key='cancel' type="default" danger onClick={() => onDelete()}> { t('buttons.delete') } </Button>,
                <Button key='submit' type="primary" onClick={() => form.submit()}> { t('buttons.update') } </Button>,
            ]}
        >
            <Flex vertical>
                <Form
                    form={form}
                    initialValues={{...appointment, date: dayjs(appointment.dDate)}}
                    layout="vertical"
                    style={{ width: '100%' }}
                    preserve
                    onFinish={handleSubmit}
                    onValuesChange={handleValuesChange}
                >
                    { renderGeneralInformation() }
                </Form>
            </Flex>
        </Modal>
    )
}

export default ReservationDetail